import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import "./SalesPages.css";

function DemoAccounts() {
  const navigate = useNavigate();

  const [demoAccounts, setDemoAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [launchingId, setLaunchingId] = useState(null);

  useEffect(() => {
    loadDemoAccounts();
  }, []);

  const filteredDemoAccounts = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    if (!term) return demoAccounts;

    return demoAccounts.filter((demo) =>
      [
        demo.demo_name,
        demo.subdomain,
        demo.full_domain,
        demo.status,
        demo.notes,
        demo.created_by_name
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [demoAccounts, searchTerm]);

  const loadDemoAccounts = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/demo-accounts`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error("Failed to load demo accounts");
      }

      const data = await res.json();
      setDemoAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load demo accounts error:", err.message);
      setDemoAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDemo = async (demoId) => {
    try {
      setLaunchingId(demoId);

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/demo-accounts/${demoId}/launch`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to launch demo account");
      }

      if (!data.launch_url) {
        throw new Error("Launch URL was not returned.");
      }

      window.open(data.launch_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Launch demo error:", err.message);
      alert(err.message);
    } finally {
      setLaunchingId(null);
    }
  };

  const statusClass = (value) =>
    `sales-chip ${String(value || "").toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Demo Accounts</h1>
            <p className="sales-subtitle">
              Manage internal demo environments separately from live subscriber reporting.
            </p>
          </div>

          <div className="sales-header-actions">
            <button className="sales-primary-btn" onClick={() => navigate("/demo-accounts/new")}>
              + New Demo Account
            </button>
          </div>
        </div>

        <div className="sales-toolbar-card">
          <input
            type="text"
            placeholder="Search demo accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="sales-search-input"
          />
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>Demo Environments</h2>
              <p>{filteredDemoAccounts.length} demo account{filteredDemoAccounts.length === 1 ? "" : "s"}</p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Demo Name</th>
                  <th>Status</th>
                  <th>Subdomain</th>
                  <th>Full Domain</th>
                  <th>Created By</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="sales-empty-cell">
                      Loading demo accounts...
                    </td>
                  </tr>
                ) : filteredDemoAccounts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="sales-empty-cell">
                      No demo accounts found.
                    </td>
                  </tr>
                ) : (
                  filteredDemoAccounts.map((demo) => (
                    <tr key={demo.id} className="sales-click-row" onClick={() => navigate(`/demo-accounts/${demo.id}`)}>
                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">{demo.demo_name || ""}</div>
                          <div className="sales-secondary">{demo.notes || ""}</div>
                        </div>
                      </td>

                      <td>
                        <span className={statusClass(demo.status || "active")}>
                          {demo.status || "active"}
                        </span>
                      </td>

                      <td>{demo.subdomain || "—"}</td>
                      <td>{demo.full_domain || "—"}</td>
                      <td>{demo.created_by_name || "—"}</td>
                      <td>{demo.expires_at ? String(demo.expires_at).slice(0, 10) : "—"}</td>

                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="sales-secondary-btn"
                          onClick={() => handleOpenDemo(demo.id)}
                          disabled={launchingId === demo.id}
                        >
                          {launchingId === demo.id ? "Opening..." : "Open Demo"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default DemoAccounts;